import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IntakeDetailSection } from "@/components/intake/IntakeDetailSection";
import { IntakeAssetCard } from "@/components/intake/IntakeAssetCard";
import { IntakeFieldRow } from "@/components/intake/IntakeFieldRow";
import { 
  ArrowLeft, 
  Battery, 
  Building2, 
  Car, 
  CheckCircle,
  AlertCircle,
  Download, 
  FileText, 
  Gauge, 
  Mail, 
  MapPin, 
  Phone, 
  Plug, 
  Sun, 
  Upload,
  User,
  Zap 
} from "lucide-react";

type SubmissionRow = {
  id: string;
  project_id: string | null;
  status: string;
  submitted_at: string;
  payload: any;
  projects?: { id?: string; name?: string | null; project_number?: string | null } | null;
};

type FileRow = {
  id: string;
  file_type: string;
  bucket: string;
  path: string;
  original_filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

const fileTypeLabels: Record<string, string> = {
  kwartierwaarden_hoofdmeter: "Kwartierwaarden hoofdmeter",
  kwartierwaarden_productie: "Kwartierwaarden productie",
  energiecontract: "Energiecontract",
  transportovereenkomst: "Transportovereenkomst",
};

const allDocumentTypes = [
  { key: 'kwartierwaarden_hoofdmeter', label: 'Kwartierwaarden hoofdmeter' },
  { key: 'kwartierwaarden_productie', label: 'Kwartierwaarden productie' },
  { key: 'energiecontract', label: 'Energiecontract' },
  { key: 'transportovereenkomst', label: 'Transportovereenkomst' },
];

export default function CRMIntakeDetail() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState<SubmissionRow | null>(null);
  const [files, setFiles] = useState<Array<FileRow & { signedUrl?: string }>>([]);
  
  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFileType, setUploadFileType] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const loadFiles = useCallback(async (submissionId: string) => {
    const { data: fileRows, error: filesError } = await supabase
      .from("external_intake_files")
      .select("id, file_type, bucket, path, original_filename, mime_type, size_bytes, created_at")
      .eq("submission_id", submissionId)
      .order("created_at", { ascending: true });

    if (filesError) throw filesError;

    const withUrls = await Promise.all(
      ((fileRows as any[]) ?? []).map(async (f) => {
        const { data } = await supabase.storage
          .from(f.bucket)
          .createSignedUrl(f.path, 60 * 60);
        return { ...f, signedUrl: data?.signedUrl };
      }),
    );

    return withUrls;
  }, []);

  const refreshFiles = useCallback(async () => {
    if (!id) return;
    try {
      const updatedFiles = await loadFiles(id);
      setFiles(updatedFiles);
    } catch (e) {
      console.error("[CRMIntakeDetail] refresh files failed", e);
    }
  }, [id, loadFiles]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;

    (async () => {
      try {
        setLoading(true);

        const { data: sub, error: subError } = await supabase
          .from("external_intake_submissions")
          .select("id, project_id, status, submitted_at, payload, projects(id, name, project_number)")
          .eq("id", id)
          .maybeSingle();

        if (subError) throw subError;
        if (!sub) {
          if (mounted) setSubmission(null);
          return;
        }
        if (mounted) setSubmission(sub as any);

        const withUrls = await loadFiles(id);
        if (mounted) setFiles(withUrls);
      } catch (e: any) {
        console.error("[CRMIntakeDetail] load failed", e);
        toast({
          title: "Fout",
          description: "Kon intake niet laden",
          variant: "destructive",
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id, toast, loadFiles]);

  const openUploadDialog = (fileType: string) => {
    setUploadFileType(fileType);
    setUploadFile(null);
    setUploadDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadFileType || !id) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('submissionId', id);
      formData.append('fileType', uploadFileType);
      formData.append('file', uploadFile);

      const { error } = await supabase.functions.invoke('intake-upload-document', {
        body: formData,
      });

      if (error) throw error;

      toast({ 
        title: "Document geüpload", 
        description: "Bestand succesvol toegevoegd." 
      });

      await refreshFiles();
      setUploadDialogOpen(false);
      setUploadFile(null);
    } catch (e: any) {
      console.error("[CRMIntakeDetail] upload failed", e);
      toast({ 
        title: "Upload mislukt", 
        description: e.message || "Er ging iets mis",
        variant: "destructive" 
      });
    } finally {
      setUploading(false);
    }
  };

  // Calculate document status
  const uploadedTypes = new Set(files.map(f => f.file_type));
  const completedDocs = allDocumentTypes.filter(d => uploadedTypes.has(d.key));
  const missingDocs = allDocumentTypes.filter(d => !uploadedTypes.has(d.key));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-muted-foreground">Laden…</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Intake niet gevonden</CardTitle>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/crm/intakes">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Terug naar lijst
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const payload = submission.payload ?? {};
  const contact = payload.contact ?? {};
  const location = payload.location ?? {};
  const connection = payload.connection ?? {};
  const metering = payload.metering ?? {};
  const solar = payload.solar ?? {};
  const battery = payload.battery ?? {};
  const charging = payload.charging ?? {};
  const generators = payload.generators ?? {};
  const notes = payload.notes ?? {};

  const formatAddress = () => {
    const parts = [
      location.street_house_number,
      location.addition,
    ].filter(Boolean);
    const line1 = parts.join(" ");
    const line2 = [location.postal_code, location.city].filter(Boolean).join(" ");
    return [line1, line2].filter(Boolean).join(", ");
  };

  const getConnectionDisplay = () => {
    if (connection.consumer_type === "klein") {
      return `Kleinverbruiker ${connection.small_option || ""}`;
    }
    const parts = [];
    if (connection.large_contract_delivery_kw) {
      parts.push(`Afname: ${connection.large_contract_delivery_kw} kW`);
    }
    if (connection.large_contract_feed_in_kw) {
      parts.push(`Teruglevering: ${connection.large_contract_feed_in_kw} kW`);
    }
    return parts.length > 0 ? `Grootverbruiker (${parts.join(", ")})` : "Grootverbruiker";
  };

  const getMeteringDisplay = () => {
    const company = metering.company === "Anders" ? metering.company_other : metering.company;
    return company || "Onbekend";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle>EMS Intake</CardTitle>
              <Badge variant={submission.status === "submitted" ? "default" : "secondary"}>
                {submission.status === "submitted" ? "Ingediend" : submission.status}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              {submission.projects?.name ?? location.name ?? "(Locatie onbekend)"}
              {submission.projects?.project_number ? ` · ${submission.projects.project_number}` : ""}
            </div>
            <div className="text-xs text-muted-foreground">
              Ingediend: {new Date(submission.submitted_at).toLocaleString("nl-NL")}
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/crm/intakes">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Lijst
              </Link>
            </Button>
            {submission.project_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/crm/projects/${submission.project_id}`}>
                  <Building2 className="h-4 w-4 mr-2" />
                  Project
                </Link>
              </Button>
            ) : null}
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Contact & Locatie */}
        <IntakeDetailSection title="Contact & Locatie" icon={User}>
          <div className="space-y-4">
            <div className="space-y-2">
              <IntakeFieldRow 
                label="Naam" 
                value={contact.name} 
              />
              <IntakeFieldRow 
                label="E-mail" 
                value={
                  contact.email ? (
                    <a href={`mailto:${contact.email}`} className="text-primary hover:underline flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {contact.email}
                    </a>
                  ) : null
                } 
              />
              <IntakeFieldRow 
                label="Telefoon" 
                value={
                  contact.phone ? (
                    <a href={`tel:${contact.phone}`} className="text-primary hover:underline flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {contact.phone}
                    </a>
                  ) : null
                } 
              />
            </div>
            <div className="pt-2 border-t">
              <IntakeFieldRow 
                label="Locatie" 
                value={location.name} 
              />
              <IntakeFieldRow 
                label="Adres" 
                value={
                  formatAddress() ? (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      {formatAddress()}
                    </span>
                  ) : null
                } 
              />
            </div>
          </div>
        </IntakeDetailSection>

        {/* Aansluiting & Metering */}
        <IntakeDetailSection title="Aansluiting & Metering" icon={Plug}>
          <div className="space-y-2">
            <IntakeFieldRow 
              label="Aansluiting" 
              value={getConnectionDisplay()} 
            />
            <IntakeFieldRow 
              label="Meetbedrijf" 
              value={getMeteringDisplay()} 
            />
            <IntakeFieldRow 
              label="Meters" 
              value={metering.meters_to_monitor ? `${metering.meters_to_monitor} meter(s)` : null} 
            />
          </div>
        </IntakeDetailSection>
      </div>

      {/* Assets */}
      <IntakeDetailSection title="Opwek & Assets" icon={Zap}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <IntakeAssetCard 
            title="Zonnepanelen" 
            icon={Sun} 
            present={solar.present === true}
          >
            <IntakeFieldRow label="Vermogen" value={solar.total_power_kwp ? `${solar.total_power_kwp} kWp` : null} />
            <IntakeFieldRow 
              label="Omvormer" 
              value={solar.inverter_brand === "Anders" ? solar.inverter_brand_other : solar.inverter_brand} 
            />
            <IntakeFieldRow label="Aantal omvormers" value={solar.inverter_count} />
          </IntakeAssetCard>

          <IntakeAssetCard 
            title="Batterij" 
            icon={Battery} 
            present={battery.present === true}
          >
            <IntakeFieldRow label="Capaciteit" value={battery.total_capacity_kwh ? `${battery.total_capacity_kwh} kWh` : null} />
            <IntakeFieldRow label="Vermogen" value={battery.total_power_kw ? `${battery.total_power_kw} kW` : null} />
            <IntakeFieldRow 
              label="Merk" 
              value={battery.brand === "Anders" ? battery.brand_other : battery.brand} 
            />
            <IntakeFieldRow label="PMS" value={battery.pms_present ? "Ja" : battery.pms_present === false ? "Nee" : null} />
          </IntakeAssetCard>

          <IntakeAssetCard 
            title="Laadinfrastructuur" 
            icon={Car} 
            present={charging.present === true}
          >
            <IntakeFieldRow label="Type" value={charging.type} />
            {charging.ac?.total_power_kw ? (
              <IntakeFieldRow 
                label="AC" 
                value={`${charging.ac.total_power_kw} kW (${charging.ac.count || "?"} laders) - ${charging.ac.brand === "Anders" ? charging.ac.brand_other : charging.ac.brand || "?"}`} 
              />
            ) : null}
            {charging.dc?.total_power_kw ? (
              <IntakeFieldRow 
                label="DC" 
                value={`${charging.dc.total_power_kw} kW (${charging.dc.count || "?"} laders) - ${charging.dc.brand === "Anders" ? charging.dc.brand_other : charging.dc.brand || "?"}`} 
              />
            ) : null}
            <IntakeFieldRow 
              label="CPO" 
              value={charging.cpo === "Anders" ? charging.cpo_other : charging.cpo} 
            />
          </IntakeAssetCard>

          <IntakeAssetCard 
            title="Generatoren" 
            icon={Gauge} 
            present={generators.present === true}
          >
            <IntakeFieldRow label="Max. vermogen" value={generators.max_total_power_kw ? `${generators.max_total_power_kw} kW` : null} />
          </IntakeAssetCard>
        </div>
      </IntakeDetailSection>

      {/* Documenten met visuele indicatoren */}
      <IntakeDetailSection title="Documenten" icon={FileText}>
        {/* Status badges */}
        <div className="mb-4 flex items-center gap-2">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            {completedDocs.length} geüpload
          </Badge>
          {missingDocs.length > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <AlertCircle className="h-3 w-3 mr-1" />
              {missingDocs.length} ontbrekend
            </Badge>
          )}
        </div>

        {/* Document grid with status indicators */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {allDocumentTypes.map((docType) => {
            const file = files.find(f => f.file_type === docType.key);
            const isUploaded = !!file;

            return (
              <div
                key={docType.key}
                className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
                  isUploaded
                    ? 'border-green-200 bg-green-50/50'
                    : 'border-amber-200 bg-amber-50/50 border-dashed'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {isUploaded ? (
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    )}
                    {docType.label}
                  </div>
                  {file && (
                    <div className="text-xs text-muted-foreground truncate ml-6">
                      {file.original_filename ?? file.path.split("/").pop()}
                    </div>
                  )}
                </div>

                {isUploaded ? (
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    disabled={!file.signedUrl}
                  >
                    <a href={file.signedUrl} target="_blank" rel="noreferrer">
                      <Download className="h-4 w-4" />
                    </a>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openUploadDialog(docType.key)}
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </IntakeDetailSection>

      {/* Notities */}
      {(notes.expected_growth || notes.sustainability_goals) ? (
        <IntakeDetailSection title="Notities" icon={FileText}>
          <div className="space-y-3">
            {notes.expected_growth ? (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Verwachte groei</div>
                <div className="text-sm">{notes.expected_growth}</div>
              </div>
            ) : null}
            {notes.sustainability_goals ? (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Duurzaamheidsdoelen</div>
                <div className="text-sm">{notes.sustainability_goals}</div>
              </div>
            ) : null}
          </div>
        </IntakeDetailSection>
      ) : null}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Document uploaden</DialogTitle>
            <DialogDescription>
              {uploadFileType && fileTypeLabels[uploadFileType]}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-upload">Bestand kiezen</Label>
              <Input
                id="file-upload"
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setUploadDialogOpen(false)}
                disabled={uploading}
              >
                Annuleren
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={!uploadFile || uploading}
              >
                {uploading ? "Uploaden..." : "Uploaden"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
